import { useState, useEffect } from "react";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";

import type {
  LandingPageAttributes,
  TouchpointVersionAttributes,
} from "hooks/api/use-touchpoint-versions";

import { convertTouchpointToUrl } from "utilities";

import { TouchpointStatuses } from "types";

import { getTouchpointVersion } from "generated/api/touchpoint-version";

const touchpointVersionApi = getTouchpointVersion();

export const useRedirectTouchpointUrl = (selectedVersion?: TouchpointVersionAttributes) => {
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const [redirectTouchpointUrl, setRedirectTouchpointUrl] = useState<string | null>(null);
  const [redirectTouchpointNoPublishedVersion, setRedirectTouchpointNoPublishedVersion] =
    useState(false);

  const selectedLP = selectedVersion as LandingPageAttributes | undefined;
  const isRedirectTouchpoint =
    selectedLP?.redirectDestinationType === "TOUCHPOINT" && selectedLP?.redirectDestination;

  useEffect(() => {
    if (!isRedirectTouchpoint) {
      return;
    }

    const touchpointId = selectedLP!.redirectDestination!;

    touchpointVersionApi
      .getAllVersions(
        {
          clientId: currentClient.id,
          campaignId: currentCampaign.id,
          touchpointId,
        },
        { unpaged: true },
      )
      .then((res) => {
        const published = res.content?.find((v) => v.status === TouchpointStatuses.PUBLISHED);
        const url = published && typeof published.url === "string" ? published.url : undefined;
        if (url) {
          setRedirectTouchpointUrl(convertTouchpointToUrl(currentClient, currentCampaign, url));
          setRedirectTouchpointNoPublishedVersion(false);
        } else {
          setRedirectTouchpointUrl(null);
          setRedirectTouchpointNoPublishedVersion(true);
        }
      })
      .catch(() => {
        setRedirectTouchpointUrl(null);
        setRedirectTouchpointNoPublishedVersion(false);
      });
  }, [isRedirectTouchpoint, selectedLP, currentClient, currentCampaign]);

  if (!isRedirectTouchpoint) {
    return { redirectTouchpointUrl: null, redirectTouchpointNoPublishedVersion: false };
  }

  return { redirectTouchpointUrl, redirectTouchpointNoPublishedVersion };
};
